import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, signal } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import type { FirebaseApp } from 'firebase/app';
import type { Messaging, MessagePayload } from 'firebase/messaging';
import * as firebaseApp from 'firebase/app';
import * as firebaseMessaging from 'firebase/messaging';
import { environment } from '../../environments/environment';
import { API_BASE_URL } from './api-config';
import { DeviceTokenSummaryDto } from '../models/device-token.models';

/** The one worker file for every environment: it has no Firebase project in it and reads the public
 * web config from its own URL, which is built here from the build environment. */
export const FIREBASE_SW_PATH = '/firebase-messaging-sw.js';

export function firebaseWorkerUrl(config: typeof environment.firebase): string {
  const q = new URLSearchParams({
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
    messagingSenderId: config.messagingSenderId,
    appId: config.appId
  });
  return `${FIREBASE_SW_PATH}?${q.toString()}`;
}

export type PushPermissionStatus = 'unsupported' | 'default' | 'granted' | 'denied';

/** The 5 Firebase SDK functions PushNotificationsService needs, behind one seam - esbuild's ESM
 * output makes the real `firebase/app`/`firebase/messaging` module namespace exports non-writable,
 * so tests can't `spyOn` them directly; this token is what tests substitute a fake for instead. */
export interface FcmGateway {
  isSupported(): Promise<boolean>;
  initializeApp(config: typeof environment.firebase): FirebaseApp;
  getMessaging(app: FirebaseApp): Messaging;
  getToken(messaging: Messaging, options: { vapidKey: string; serviceWorkerRegistration: ServiceWorkerRegistration }): Promise<string>;
  onMessage(messaging: Messaging, callback: (payload: MessagePayload) => void): () => void;
}

export const FCM_GATEWAY = new InjectionToken<FcmGateway>('FCM_GATEWAY', {
  factory: () => ({
    isSupported: firebaseMessaging.isSupported,
    initializeApp: firebaseApp.initializeApp,
    getMessaging: firebaseMessaging.getMessaging,
    getToken: firebaseMessaging.getToken,
    onMessage: firebaseMessaging.onMessage
  })
});

/**
 * Wires up Web Push via Firebase Cloud Messaging: requests browser notification permission,
 * registers public/firebase-messaging-sw.js (with the environment's Firebase config in its URL), obtains an FCM token, and registers it against
 * POST /users/me/device-tokens (DeviceTokenService) so NotificationDispatchService's Push channel
 * has somewhere to actually deliver to - that endpoint already existed with zero caller before this.
 *
 * firebase-messaging-sw.js only fires while the tab is in the background/closed - the FCM SDK never
 * auto-displays a notification for a page that's in the foreground, it just hands the payload to
 * onMessage(). Without a listener that message is silently dropped (NotificationDispatchService logs
 * "Sent" because FCM did accept it - the gap is purely client-side), so registerCurrentToken() also
 * subscribes onMessage() and shows the notification itself via the same ServiceWorkerRegistration.
 *
 * No-ops entirely (status stays 'unsupported') until environment.firebase.apiKey is filled in with
 * a real Firebase Web app config - see the REPLACE_ME comment in environment.dev.ts. This mirrors
 * the API side's own "activate only once configured" pattern for Smtp/Firebase (Program.cs).
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationsService {
  readonly status = signal<PushPermissionStatus>(this.readInitialStatus());
  readonly registering = signal(false);
  readonly lastError = signal<string | null>(null);

  private app: FirebaseApp | null = null;
  private messaging: Messaging | null = null;
  private foregroundListenerAttached = false;

  constructor(private http: HttpClient, @Inject(FCM_GATEWAY) private fcm: FcmGateway) {}

  get isConfigured(): boolean {
    return !!environment.firebase.apiKey && environment.firebase.apiKey !== 'REPLACE_ME';
  }

  private get browserSupportsNotifications(): boolean {
    return typeof Notification !== 'undefined' && 'serviceWorker' in navigator;
  }

  private readInitialStatus(): PushPermissionStatus {
    if (!this.isConfigured || !this.browserSupportsNotifications) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  /**
   * Call once on app init after a successful login - if the user already granted permission in an
   * earlier session, silently re-registers the device token (refreshing DeviceToken.LastSeenAt)
   * without prompting. Browsers never re-show the permission prompt once a decision is made, so
   * this never surfaces anything to the user - it's safe to call unconditionally on every login.
   */
  async refreshIfAlreadyGranted(): Promise<void> {
    if (this.status() === 'granted') {
      await this.registerCurrentToken();
    }
  }

  /** Call only from a real user gesture (a button click) - browsers only allow the permission
   * prompt to actually appear in response to one; calling this from app init would either silently
   * do nothing or be flagged as an abusive pattern. */
  async enable(): Promise<void> {
    if (!this.isConfigured || !this.browserSupportsNotifications || !(await this.fcm.isSupported())) {
      this.status.set('unsupported');
      return;
    }

    this.lastError.set(null);
    const permission = await Notification.requestPermission();
    this.status.set(permission);
    if (permission === 'granted') {
      await this.registerCurrentToken();
    }
  }

  private async registerCurrentToken(): Promise<void> {
    this.registering.set(true);
    try {
      const registration = await navigator.serviceWorker.register(firebaseWorkerUrl(environment.firebase));
      const messaging = this.getMessaging();
      const token = await this.fcm.getToken(messaging, {
        vapidKey: environment.firebase.vapidKey,
        serviceWorkerRegistration: registration
      });
      if (token) {
        await firstValueFrom(this.http.post<void>(`${API_BASE_URL}/users/me/device-tokens`, { token, platform: 'web' }));
      }
      this.attachForegroundListener(messaging, registration);
    } catch {
      this.lastError.set('push-registration-failed');
    } finally {
      this.registering.set(false);
    }
  }

  /** Only ever attached once per page load - refreshIfAlreadyGranted() re-registers the token on
   * every login, which would otherwise stack up duplicate listeners and show each push N times. */
  private attachForegroundListener(messaging: Messaging, registration: ServiceWorkerRegistration): void {
    if (this.foregroundListenerAttached) {
      return;
    }
    this.foregroundListenerAttached = true;
    this.fcm.onMessage(messaging, (payload) => {
      const title = payload.notification?.title ?? '';
      const body = payload.notification?.body;
      void registration.showNotification(title, { body });
    });
  }

  /** Self-service "which devices am I registered on" - the only way, from the UI, to notice that stale
   * registrations from an old browser/reinstall have piled up rather than being replaced. */
  listDevices(): Observable<DeviceTokenSummaryDto[]> {
    return this.http.get<DeviceTokenSummaryDto[]>(`${API_BASE_URL}/users/me/device-tokens`);
  }

  removeDevice(id: string): Observable<void> {
    return this.http.delete<void>(`${API_BASE_URL}/users/me/device-tokens/${id}`);
  }

  private getMessaging(): Messaging {
    this.app ??= this.fcm.initializeApp(environment.firebase);
    this.messaging ??= this.fcm.getMessaging(this.app);
    return this.messaging;
  }
}
